import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfigModel, PageModel } from '@libs/cms/models';
import { CmsLanguageService } from '@libs/shared/common';
import { AppError, ConnectionParams, NavItemModel } from '@libs/shared/models';
import { StatusCodes } from 'http-status-codes';
import { defer, map, Observable, shareReplay } from 'rxjs';
import { ConnectorService } from '../connector';

@Injectable({ providedIn: 'root' })
export class CmsRepositoryService {
  private readonly connector: ConnectorService = inject(ConnectorService);
  private readonly cmsLanguageService: CmsLanguageService = inject(CmsLanguageService);

  // API v2
  public getAppConfig(): Observable<AppConfigModel> {
    return this.connector.connect('cms', 'configuration', this.getLanguageParams());
  }

  public getAppContent(contentPath: string): Observable<PageModel> {
    const searchParams: ConnectionParams['searchParams'] = { path: contentPath };

    const response$: Observable<HttpResponse<PageModel>> = this.connector.connect<HttpResponse<PageModel>>('cms', 'content', {
      ...this.getLanguageParams(contentPath),
      observe: 'response',
      searchParams,
    }).pipe(
      shareReplay(1),
    );

    return defer((): Observable<PageModel> => {
      const requestPath: string | undefined = this.cmsLanguageService.getRequestPath();

      return response$.pipe(
        map((response: HttpResponse<PageModel>): PageModel => {
          // A response adopts its language only while the path it was requested for is still current;
          // a cached response is matched against the path of its new subscription.
          if (requestPath === this.cmsLanguageService.getRequestPath()) {
            this.cmsLanguageService.storeResponseLanguage(response.headers.get('content-language'));
          }

          if (!response.body) {
            throw AppError.create('CmsRepositoryService', StatusCodes.NO_CONTENT, contentPath);
          }

          return response.body;
        }),
      );
    });
  }

  public submitAppForm(formId: string, formData: FormData): Observable<undefined> {
    const pathParams: Record<string, string> = { formId };
    const payload: FormData = formData;

    return this.connector.connect('cms', 'form', { ...this.getLanguageParams(), pathParams, payload });
  }

  // API v1
  public getPage(sectionId: string, contentId: string | number, customerNumber?: string): Observable<PageModel> {
    const params: ConnectionParams = this.formatParams(contentId, customerNumber, sectionId);

    return this.connector.connect('cms', 'page', params);
  }

  public getStructure(contentId: string | number, customerNumber?: string): Observable<NavItemModel> {
    const params: ConnectionParams = this.formatParams(contentId, customerNumber, undefined);

    return this.connector.connect<NavItemModel>('cms', 'structure', params);
  }

  private formatParams(contentId: string | number, customerNumber?: string, sectionId?: string): ConnectionParams {
    const params: ConnectionParams = {
      pathParams: { contentId: contentId.toString() },
    };

    if (sectionId) {
      params.pathParams = {
        ...params.pathParams,
        sectionId,
      };
    }

    if (customerNumber) {
      params.searchParams = {
        'customer-number': customerNumber,
      };
    }

    return params;
  }

  private getLanguageParams(contentPath?: string): ConnectionParams {
    const language: string | undefined = this.cmsLanguageService.getRequestLanguage(contentPath);

    // without a resolved language the request falls back to the active application language
    return { headers: language ? { 'accept-language': language } : undefined };
  }
}
